import React, { useEffect } from "react";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Grid from "@mui/material/Grid";
import {
  SelectField,
  InputField,
  NumberField,
  DateField,
  ReadOnlyField,
  Checkbox,
  InfoNote,
  PriorSubmissionBanner,
  SectionSubheader,
  CharCount
} from "./primitives";
import { entryFieldsFromPriorSubagency, createEmptyEntry } from "./data";
import Link from "@mui/material/Link";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { colors } from "../../utils/styles";

const directChargesPortalUrl = process.env.REACT_APP_DIRECT_CHARGES_PORTAL_URL;

function SubagencyCard({
  entry,
  index,
  total,
  subs,
  subagencyData,
  selectedCodes,
  taxRollYear,
  expanded,
  onToggle,
  onUpdate,
  onRemove
}) {
  const selSub = subs.find((s) => s.code === entry.subagency);
  const label = selSub ? selSub.name : `Subagency ${index + 1}`;
  const pathFor = (field) => `entries[${entry.id}].${field}`;

  const { contactsByCode, latestByCode, loadContacts, loadLatest, prefilledCodesRef } = subagencyData;
  const code = entry.subagency;
  const subContacts = code ? contactsByCode[code] || [] : [];
  const latestSubmission = code ? latestByCode[code] || null : null;

  useEffect(() => {
    if (!code) return;
    loadContacts(code);
    loadLatest(code);
  }, [code, loadContacts, loadLatest]);

  // Prefill from the prior submission once per code (see prefilledCodesRef
  // declaration in PreliminaryNotification.jsx). The pristine-entry guard
  // handles the load race: if the user types while `loadLatest` is still in
  // flight, we don't clobber their edits when the fetch resolves.
  useEffect(() => {
    if (!code || !latestSubmission || prefilledCodesRef.current.has(code)) return;
    const prior = latestSubmission.subagencies?.[0];
    if (!prior) return;
    prefilledCodesRef.current.add(code);
    const pristine = createEmptyEntry(entry.id);
    const userEdited = Object.keys(pristine).some(
      (k) => k !== "id" && k !== "subagency" && entry[k] !== pristine[k]
    );
    if (!userEdited) onUpdate(entry.id, entryFieldsFromPriorSubagency(prior));
  }, [code, latestSubmission, entry, entry.id, onUpdate, prefilledCodesRef]);

  const handleSubagencyChange = (newCode) => {
    // Reset all other fields so the new subagency starts clean; the prefill effect
    // above will repopulate from a prior submission if one exists. Forget the new
    // code's prefilled state so re-selecting a previously chosen code re-prefills.
    if (newCode) prefilledCodesRef.current.delete(newCode);
    onUpdate(entry.id, { ...createEmptyEntry(entry.id), subagency: newCode });
  };

  // A sub is selectable if it's already this row's selection or no other row has claimed it.
  const availableSubs = subs.filter((s) => s.code === entry.subagency || !selectedCodes.has(s.code));
  const subOptions = availableSubs
    .slice()
    .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
    .map((s) => ({ value: s.code, label: `${s.name} (${s.code})` }));

  return (
    <div
      style={{
        border: `1px solid ${expanded ? colors.gold : colors.borderLight}`,
        borderRadius: 10,
        background: colors.white,
        overflow: "hidden",
        transition: "border-color 0.2s"
      }}
    >
      <div
        onClick={() => onToggle(entry.id)}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "14px 20px",
          cursor: "pointer",
          background: expanded ? `${colors.gold}0A` : colors.white,
          borderBottom: expanded ? `1px solid ${colors.borderLight}` : "none"
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: 6,
              background: expanded ? colors.navy : colors.sectionBg,
              color: expanded ? colors.white : colors.textMuted,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 13,
              fontWeight: 700,
              flexShrink: 0
            }}
          >
            {index + 1}
          </div>
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: colors.navy,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis"
              }}
            >
              {label}
            </div>
            {selSub && (
              <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 1 }}>
                {selSub.code}
                {entry.chargeCategory && <> · {entry.chargeCategory}</>}
              </div>
            )}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
          <div
            style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: selSub ? colors.success : colors.textLight
            }}
          />
          {total > 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onRemove(entry.id);
              }}
              title="Remove this subagency"
              style={{
                width: 28,
                height: 28,
                borderRadius: 6,
                border: `1px solid ${colors.borderLight}`,
                background: colors.white,
                color: colors.error,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 16,
                lineHeight: 1
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = colors.errorBg)}
              onMouseLeave={(e) => (e.currentTarget.style.background = colors.white)}
            >
              ×
            </button>
          )}
          <svg
            width="18"
            height="18"
            viewBox="0 0 20 20"
            fill="none"
            stroke={colors.textMuted}
            strokeWidth="2"
            strokeLinecap="round"
            style={{ transform: expanded ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }}
          >
            <polyline points="5,8 10,13 15,8" />
          </svg>
        </div>
      </div>

      {expanded && (
        <div style={{ padding: "20px 20px 24px", display: "flex", flexDirection: "column", gap: 30 }}>
          <PriorSubmissionBanner submission={latestSubmission} />
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <SectionSubheader title="I. Subagency Information" />
            <Grid container spacing={2.5}>
              <Grid size={{ xs: 12 }}>
                <SelectField
                  label="Subagency Name"
                  required
                  value={entry.subagency}
                  onChange={(e) => handleSubagencyChange(e.target.value)}
                  options={subOptions}
                  placeholder={subs.length ? "Select subagency..." : "Select a parent agency first"}
                >
                  {subs.length > 0 && availableSubs.length === 0 && !entry.subagency && (
                    <p style={{ margin: "6px 0 0", fontSize: 12, color: colors.error }}>
                      All available subagencies have been added.
                    </p>
                  )}
                </SelectField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <SelectField
                  label="Account Type"
                  required
                  path={pathFor("accountType")}
                  helpText={`Select New if this is a new subagency for ${taxRollYear}. New subagencies must be requested through the Direct Charges portal by the stated deadline.`}
                  value={entry.accountType}
                  onChange={(e) => onUpdate(entry.id, { accountType: e.target.value })}
                  options={[
                    { value: "New", label: "New" },
                    { value: "Existing", label: "Existing" }
                  ]}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <SelectField
                  label="Active?"
                  required
                  path={pathFor("active")}
                  value={entry.active}
                  onChange={(e) => onUpdate(entry.id, { active: e.target.value })}
                  options={[
                    { value: "Yes", label: "Yes" },
                    { value: "No", label: "No" }
                  ]}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <ReadOnlyField label="Address" value={selSub?.address} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <ReadOnlyField label="Public Contact on Tax Bill" value={selSub?.phone} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }} sx={{ display: "flex", alignItems: "end" }}>
                <InfoNote>
                  Updates to the <strong>Public Contact on Tax Bill</strong> must be done through the{" "}
                  <Link
                    href={directChargesPortalUrl}
                    color="primary"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Direct Charges Portal
                    <OpenInNewIcon fontSize="inherit" sx={{ verticalAlign: "middle", ml: 0.5, mb: 0.35 }} />
                  </Link>
                  .
                </InfoNote>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  <ReadOnlyField label="Description on Tax Bill" value={selSub?.name} />
                  <Checkbox
                    label="Request Description Update"
                    checked={entry.reqDescUpdate}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      onUpdate(
                        entry.id,
                        checked ? { reqDescUpdate: true } : { reqDescUpdate: false, newDescription: "" }
                      );
                    }}
                  />
                </div>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                {entry.reqDescUpdate && (
                  <InputField
                    label="New Description"
                    required
                    path={pathFor("newDescription")}
                    value={entry.newDescription}
                    onChange={(e) => onUpdate(entry.id, { newDescription: e.target.value })}
                    placeholder="Enter new description..."
                    maxLength={50}
                  >
                    <CharCount value={entry.newDescription} max={50} />
                  </InputField>
                )}
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <SelectField
                  label="Charge Status"
                  required
                  path={pathFor("chargeStatus")}
                  value={entry.chargeStatus}
                  onChange={(e) => onUpdate(entry.id, { chargeStatus: e.target.value })}
                  options={[
                    { value: "New", label: "New" },
                    { value: "Existing", label: "Existing" }
                  ]}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <SelectField
                  label="Charge Category"
                  required
                  path={pathFor("chargeCategory")}
                  value={entry.chargeCategory}
                  onChange={(e) => onUpdate(entry.id, { chargeCategory: e.target.value })}
                  options={[
                    { value: "Special Taxes", label: "Special Taxes" },
                    { value: "Assessments", label: "Assessments" },
                    { value: "Fees & Charges", label: "Fees & Charges" },
                    { value: "Delinquent Charges", label: "Delinquent Charges" },
                    { value: "Abatement", label: "Abatement" },
                    { value: "Other", label: "Other" }
                  ]}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <InputField
                  label="Service Description"
                  required
                  path={pathFor("serviceDesc")}
                  value={entry.serviceDesc}
                  onChange={(e) => onUpdate(entry.id, { serviceDesc: e.target.value })}
                  placeholder="Describe the service..."
                  maxLength={500}
                  multiline
                  minRows={1}
                >
                  <CharCount value={entry.serviceDesc} max={500} />
                </InputField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <NumberField
                  label="Estimated Parcels"
                  required
                  path={pathFor("estParcels")}
                  value={entry.estParcels}
                  onChange={(e) => onUpdate(entry.id, { estParcels: e.target.value })}
                  placeholder="e.g. 1,500"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <NumberField
                  label="Estimated Amount"
                  required
                  path={pathFor("estAmount")}
                  value={entry.estAmount}
                  onChange={(e) => onUpdate(entry.id, { estAmount: e.target.value })}
                  placeholder="e.g. $250,000"
                  prefix="$"
                />
              </Grid>
            </Grid>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <SectionSubheader title="II. Legal Authority" />
            <Grid container spacing={2.5}>
              <Grid size={{ xs: 12 }}>
                <InputField
                  label="Enabling Statute/Code Section"
                  required
                  path={pathFor("enablingStatute")}
                  value={entry.enablingStatute}
                  onChange={(e) => onUpdate(entry.id, { enablingStatute: e.target.value })}
                  placeholder="e.g. Gov. Code §53311 et seq."
                  maxLength={500}
                  multiline
                  minRows={1}
                >
                  <CharCount value={entry.enablingStatute} max={500} />
                </InputField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <SelectField
                  label="Legal Authority Type"
                  required
                  path={pathFor("legalAuthorityType")}
                  value={entry.legalAuthorityType}
                  onChange={(e) => onUpdate(entry.id, { legalAuthorityType: e.target.value })}
                  options={[
                    { value: "State Statute", label: "State Statute" },
                    { value: "Local Ordinance", label: "Local Ordinance" },
                    { value: "Voter-Approved Measure", label: "Voter-Approved Measure" },
                    { value: "Property Owner-Approved Measure", label: "Property Owner-Approved Measure" }
                  ]}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <SelectField
                  label="Approval Method"
                  required
                  path={pathFor("approvalMethod")}
                  value={entry.approvalMethod}
                  onChange={(e) =>
                    onUpdate(entry.id, {
                      approvalMethod: e.target.value,
                      electionDate: "",
                      approvalDate: "",
                      adoptionType: "",
                      adoptionDate: "",
                      origAdoptionDate: ""
                    })
                  }
                  options={[
                    { value: "Voter Approved", label: "Voter Approved" },
                    { value: "Property Owner Approved", label: "Property Owner Approved" },
                    { value: "Board Adopted", label: "Board Adopted" }
                  ]}
                />
              </Grid>
              {entry.approvalMethod === "Voter Approved" && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <DateField
                    label="Election Date"
                    required
                    path={pathFor("electionDate")}
                    value={entry.electionDate}
                    onChange={(e) => onUpdate(entry.id, { electionDate: e.target.value })}
                  />
                </Grid>
              )}
              {entry.approvalMethod === "Property Owner Approved" && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <DateField
                    label="Approval Date"
                    required
                    path={pathFor("approvalDate")}
                    value={entry.approvalDate}
                    onChange={(e) => onUpdate(entry.id, { approvalDate: e.target.value })}
                  />
                </Grid>
              )}
              {entry.approvalMethod === "Board Adopted" && (
                <>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <SelectField
                      label="Adoption Type"
                      required
                      path={pathFor("adoptionType")}
                      value={entry.adoptionType}
                      onChange={(e) =>
                        onUpdate(entry.id, {
                          adoptionType: e.target.value,
                          adoptionDate: "",
                          origAdoptionDate: ""
                        })
                      }
                      options={[
                        { value: "Annual", label: "Annual" },
                        { value: "Evergreen", label: "Evergreen" }
                      ]}
                    />
                  </Grid>
                  {entry.adoptionType === "Annual" && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <DateField
                        label="Adoption Date"
                        required
                        path={pathFor("adoptionDate")}
                        value={entry.adoptionDate}
                        onChange={(e) => onUpdate(entry.id, { adoptionDate: e.target.value })}
                      />
                    </Grid>
                  )}
                  {entry.adoptionType === "Evergreen" && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <DateField
                        label="Original Adoption Date"
                        required
                        path={pathFor("origAdoptionDate")}
                        value={entry.origAdoptionDate}
                        onChange={(e) => onUpdate(entry.id, { origAdoptionDate: e.target.value })}
                      />
                    </Grid>
                  )}
                </>
              )}
              <Grid size={{ xs: 12, sm: 6 }}>
                <SelectField
                  label="Is Legal Authority currently in place?"
                  required
                  path={pathFor("legalInPlace")}
                  value={entry.legalInPlace}
                  onChange={(e) => onUpdate(entry.id, { legalInPlace: e.target.value })}
                  options={[
                    { value: "Yes", label: "Yes" },
                    { value: "No", label: "No" }
                  ]}
                />
              </Grid>
            </Grid>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <SectionSubheader title="III. Contacts" />
            <InfoNote>
              Updates to <strong>contact information</strong> must be made through the{" "}
              <Link href={directChargesPortalUrl} color="primary" target="_blank" rel="noopener noreferrer">
                Direct Charges Portal
                <OpenInNewIcon fontSize="inherit" sx={{ verticalAlign: "middle", ml: 0.5, mb: 0.35 }} />
              </Link>
              . The Public Contact name is not displayed on the tax bill; only the phone number is shown.
            </InfoNote>
            {subContacts.length > 0 ? (
              <div style={{ display: "grid", gap: 10 }}>
                {[...subContacts]
                  .sort((a, b) => {
                    if (a.type === b.type) return 0;
                    return a.type === "Public Contact" ? -1 : 1;
                  })
                  .map((c, i) => {
                    const isPublic = c.type === "Public Contact";
                    return (
                      <div
                        key={i}
                        style={{
                          padding: 14,
                          borderRadius: 8,
                          border: `1px solid ${isPublic ? colors.goldLight : colors.borderLight}`,
                          background: isPublic ? colors.goldPale : colors.white
                        }}
                      >
                        <Stack
                          direction={{ xs: "column-reverse", sm: "row" }}
                          justifyContent="space-between"
                          alignItems="flex-start"
                          spacing={1}
                          sx={{ marginBottom: 1 }}
                        >
                          <div style={{ fontSize: 13, fontWeight: 600, color: colors.navy }}>{c.name}</div>
                          <Chip
                            label={c.type}
                            sx={{
                              height: 20,
                              flexShrink: 0,
                              alignSelf: "flex-start",
                              background: isPublic ? colors.gold : colors.navyLight,
                              color: colors.white,
                              borderRadius: 1,
                              "& .MuiChip-label": {
                                fontSize: 10,
                                fontWeight: 700,
                                textTransform: "uppercase",
                                letterSpacing: "0.04em",
                                padding: "0 8px"
                              }
                            }}
                          />
                        </Stack>
                        <div
                          style={{
                            display: "grid",
                            gap: 6,
                            fontSize: 12,
                            color: colors.textMuted,
                            wordBreak: "break-word"
                          }}
                        >
                          <div>
                            <span style={{ fontWeight: 600 }}>Phone:</span> {c.phone || "—"}
                          </div>
                          {!isPublic && (
                            <>
                              <div>
                                <span style={{ fontWeight: 600 }}>Email:</span> {c.email || "—"}
                              </div>
                              <div>
                                <span style={{ fontWeight: 600 }}>Address:</span> {c.address || "—"}
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
              </div>
            ) : (
              <div
                style={{
                  padding: 20,
                  background: colors.sectionBg,
                  borderRadius: 8,
                  textAlign: "center",
                  color: colors.textMuted,
                  fontSize: 13
                }}
              >
                {entry.subagency
                  ? "No contacts found for this subagency."
                  : "Select a subagency above to view contacts."}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default React.memo(SubagencyCard);
